import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Smartphone, 
  Key, 
  QrCode,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Copy,
  Eye,
  EyeOff,
  Download,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const TwoFactorAuth = ({ user, onUpdate }) => {
  const [twoFactorStatus, setTwoFactorStatus] = useState(null);
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // קבלת סטטוס 2FA
  useEffect(() => {
    fetchTwoFactorStatus();
  }, []);

  const fetchTwoFactorStatus = async () => {
    try {
      const response = await fetch('/api/auth/2fa-status', {
        credentials: 'include',
      });
      const data = await response.json();
      
      if (data.success) {
        setTwoFactorStatus(data.data);
      }
    } catch (error) {
      console.log('Error fetching 2FA status:', error);
    }
  };

  // הגדרת 2FA
  const handleSetup2FA = async () => {
    if (user.role !== 'admin') {
      toast.error('רק מנהלים יכולים להגדיר 2FA');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/setup-2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          method: 'totp',
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSetupData(data.data);
        setIsSetupMode(true);
        toast.success('2FA הוגדר בהצלחה! סרוק את הQR Code');
      } else {
        toast.error(data.message || 'שגיאה בהגדרת 2FA');
      }
    } catch (error) {
      toast.error('שגיאה בהגדרת 2FA');
      console.log('Error setting up 2FA:', error);
    } finally {
      setLoading(false);
    }
  };

  // אימות 2FA
  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('הכנס קוד 6 ספרות');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/verify-2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token: verificationCode,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('2FA אומת בהצלחה!');
        setIsSetupMode(false);
        setVerificationCode('');
        fetchTwoFactorStatus();
        onUpdate && onUpdate();
      } else {
        toast.error(data.message || 'קוד 2FA לא תקין');
      }
    } catch (error) {
      toast.error('שגיאה באימות 2FA');
      console.log('Error verifying 2FA:', error);
    } finally {
      setLoading(false);
    }
  };

  // ביטול 2FA
  const handleDisable2FA = async () => {
    if (!password) {
      toast.error('הכנס סיסמה לביטול 2FA');
      return;
    }

    const confirmed = window.confirm('האם אתה בטוח שברצונך לבטל את 2FA? זה יפחית את רמת האבטחה');
    if (!confirmed) return;

    setLoading(true);
    try {
      const response = await fetch('/api/auth/disable-2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          password,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('2FA בוטל בהצלחה');
        setPassword('');
        fetchTwoFactorStatus();
        onUpdate && onUpdate();
      } else {
        toast.error(data.message || 'שגיאה בביטול 2FA');
      }
    } catch (error) {
      toast.error('שגיאה בביטול 2FA');
      console.log('Error disabling 2FA:', error);
    } finally {
      setLoading(false);
    }
  };

  // העתקת קוד backup
  const copyBackupCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('קוד הועתק ללוח');
  };

  // הורדת קודי backup
  const downloadBackupCodes = () => {
    if (!setupData || !setupData.backupCodes) return;
    
    const content = `SHA-SHA 2FA Backup Codes\n\n${setupData.backupCodes.join('\n')}\n\nאחסן קודים אלה במקום בטוח!\nכל קוד יכול לשמש פעם אחת בלבד.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sha-sha-backup-codes.txt';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('קודי הגיבוי הורדו');
  };

  if (user.role !== 'admin') {
    return (
      <div className="bg-red-900/20 backdrop-blur-sm rounded-2xl p-6 border border-red-500/30">
        <div className="flex items-center space-x-3 mb-4">
          <AlertTriangle className="text-red-400 w-6 h-6" />
          <h2 className="text-xl font-bold text-red-300">גישה מוגבלת</h2>
        </div>
        <p className="text-red-200">
          2FA זמין רק למנהלים. רק משתמשים עם הרשאות מנהל יכולים להגדיר אימות דו-שלבי.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50">
      <div className="flex items-center space-x-3 mb-6">
        <Shield className="text-blue-400 w-6 h-6" />
        <h2 className="text-2xl font-bold text-white">אימות דו-שלבי (2FA)</h2>
      </div>

      {/* סטטוס 2FA */}
      {twoFactorStatus && (
        <div className={`rounded-xl p-4 mb-6 border ${
          twoFactorStatus.enabled 
            ? 'bg-green-900/30 border-green-500/20' 
            : 'bg-red-900/30 border-red-500/20'
        }`}>
          <div className="flex items-center space-x-3">
            {twoFactorStatus.enabled ? (
              <CheckCircle className="text-green-400 w-6 h-6" />
            ) : (
              <XCircle className="text-red-400 w-6 h-6" />
            )}
            <div>
              <h3 className={`font-bold ${
                twoFactorStatus.enabled ? 'text-green-300' : 'text-red-300'
              }`}>
                {twoFactorStatus.enabled ? '2FA מופעל' : '2FA לא מופעל'}
              </h3>
              <p className={`text-sm ${
                twoFactorStatus.enabled ? 'text-green-200' : 'text-red-200'
              }`}>
                {twoFactorStatus.enabled 
                  ? `הוגדר: ${new Date(twoFactorStatus.setupAt).toLocaleDateString('he-IL')}`
                  : 'החשבון שלך לא מאובטח באימות דו-שלבי'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* הגדרת 2FA */}
      {!twoFactorStatus?.enabled && !isSetupMode && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="bg-blue-900/20 rounded-xl p-4 border border-blue-500/20">
            <h3 className="text-lg font-bold text-blue-300 mb-2">
              מה זה אימות דו-שלבי?
            </h3>
            <p className="text-blue-200 text-sm mb-4">
              2FA מוסיף שכבת אבטחה נוספת לחשבון שלך. בנוסף לסיסמה, תצטרך להכניס קוד מהטלפון שלך.
            </p>
            <div className="flex items-center space-x-3 mb-4">
              <Smartphone className="text-blue-400 w-5 h-5" />
              <span className="text-blue-200 text-sm">
                נדרש אפליקציית Google Authenticator או דומה
              </span>
            </div>
          </div>

          <motion.button
            onClick={handleSetup2FA}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Shield className="w-5 h-5" />
                <span>הגדר 2FA</span>
              </>
            )}
          </motion.button>
        </motion.div>
      )}

      {/* מצב הגדרה */}
      {isSetupMode && setupData && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="space-y-6"
        >
          <div className="bg-yellow-900/20 rounded-xl p-4 border border-yellow-500/20">
            <div className="flex items-center space-x-3 mb-3">
              <AlertTriangle className="text-yellow-400 w-5 h-5" />
              <h3 className="text-lg font-bold text-yellow-300">שלב 1: סרוק QR Code</h3>
            </div>
            <p className="text-yellow-200 text-sm mb-4">
              פתח את אפליקציית Google Authenticator וסרוק את הקוד:
            </p>
            
            <div className="flex justify-center mb-4">
              <div className="bg-white p-4 rounded-xl">
                <QrCode className="w-40 h-40 text-black" />
                <div className="text-center mt-2 text-xs text-gray-600">
                  {setupData.qrCode}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-purple-900/20 rounded-xl p-4 border border-purple-500/20">
            <div className="flex items-center space-x-3 mb-3">
              <Key className="text-purple-400 w-5 h-5" />
              <h3 className="text-lg font-bold text-purple-300">שלב 2: הכנס קוד אימות</h3>
            </div>
            <p className="text-purple-200 text-sm mb-4">
              הכנס את הקוד בן 6 הספרות מהאפליקציה:
            </p>
            
            <div className="flex space-x-2">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="flex-1 bg-gray-700 text-white placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 text-center text-2xl font-mono"
                maxLength="6"
              />
              <motion.button
                onClick={handleVerify2FA}
                disabled={loading || verificationCode.length !== 6}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl transition-colors duration-200 flex items-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <CheckCircle className="w-5 h-5" />
                )}
              </motion.button>
            </div>
          </div>

          {/* קודי backup */}
          <div className="bg-orange-900/20 rounded-xl p-4 border border-orange-500/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Download className="text-orange-400 w-5 h-5" />
                <h3 className="text-lg font-bold text-orange-300">קודי גיבוי</h3>
              </div>
              <button
                onClick={downloadBackupCodes}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-colors duration-200"
              >
                הורד
              </button>
            </div>
            <p className="text-orange-200 text-sm mb-4">
              שמור קודים אלה במקום בטוח! כל קוד יכול לשמש פעם אחת בלבד.
            </p>
            
            <div className="grid grid-cols-2 gap-2">
              {setupData.backupCodes.map((code, index) => (
                <div
                  key={index}
                  className="bg-gray-700 rounded-lg p-3 flex items-center justify-between"
                >
                  <span className="font-mono text-sm text-gray-300">{code}</span>
                  <button
                    onClick={() => copyBackupCode(code)}
                    className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* ביטול 2FA */}
      {twoFactorStatus?.enabled && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="bg-red-900/20 rounded-xl p-4 border border-red-500/20">
            <div className="flex items-center space-x-3 mb-3">
              <Trash2 className="text-red-400 w-5 h-5" />
              <h3 className="text-lg font-bold text-red-300">בטל 2FA</h3>
            </div>
            <p className="text-red-200 text-sm mb-4">
              הכנס את הסיסמה שלך לביטול 2FA (לא מומלץ):
            </p>
            
            <div className="flex space-x-2">
              <div className="relative flex-1">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="סיסמה"
                  className="w-full bg-gray-700 text-white placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-3 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <motion.button
                onClick={handleDisable2FA}
                disabled={loading || !password}
                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl transition-colors duration-200 flex items-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
              </motion.button>
            </div>
          </div>

          {/* סטטיסטיקות */}
          <div className="bg-gray-700/50 rounded-xl p-4">
            <h3 className="text-lg font-bold text-gray-300 mb-3">סטטיסטיקות</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">
                  {twoFactorStatus.method?.toUpperCase() || 'TOTP'}
                </div>
                <div className="text-sm text-gray-400">סוג 2FA</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">
                  {twoFactorStatus.backupCodesRemaining || 0}
                </div>
                <div className="text-sm text-gray-400">קודי גיבוי</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">
                  {twoFactorStatus.lastUsed 
                    ? new Date(twoFactorStatus.lastUsed).toLocaleDateString('he-IL')
                    : 'אף פעם'
                  }
                </div>
                <div className="text-sm text-gray-400">שימוש אחרון</div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default TwoFactorAuth; 